import type { ReactNode } from "react";
import {
  Animated,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from "react-native";

type ViewStyleWithWebExtras = ViewStyle & {
  backdropFilter?: string;
  WebkitBackdropFilter?: string;
};

if (Platform.OS === "web" && typeof document !== "undefined") {
  const id = "invite-glass-card-styles";
  if (!document.getElementById(id)) {
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes inviteTitleShimmer {
        0% { background-position: 200% 200%; }
        50% { background-position: 0% 0%; }
        100% { background-position: 200% 200%; }
      }
      .invite-glass-card {
        position: relative;
        border: none !important;
      }
      .invite-glass-card::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 28px;
        padding: 1px;
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0.22) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.02) 100%
        );
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }
      @media (max-width: 599px) {
        .invite-glass-card::before {
          border-radius: 260px 260px 28px 28px;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export type InviteGlassCardProps = {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
};

export function InviteGlassCard({ children, style }: InviteGlassCardProps) {
  const { width } = useWindowDimensions();
  const isCompact = width < 600;

  return (
    <Animated.View
      {...(Platform.OS === "web" ? { className: "invite-glass-card" } : {})}
      style={[
        styles.card,
        isCompact && styles.cardCompact,
        Platform.OS === "web" && styles.cardWeb,
        style,
      ]}
    >
      <View style={styles.orbSpacer} />
      <View style={styles.content}>{children}</View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 28,
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    width: "100%",
    maxWidth: 520,
    paddingBottom: 24,
  },
  cardCompact: {
    maxWidth: "100%",
    borderRadius: 28,
    borderTopLeftRadius: 260,
    borderTopRightRadius: 260,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  },
  cardWeb: {
    backdropFilter: "blur(60px) saturate(1.8) brightness(0.9)",
    WebkitBackdropFilter: "blur(60px) saturate(1.8) brightness(0.9)",
    backgroundColor: "rgba(255, 255, 255, 0.04)",
  } as ViewStyleWithWebExtras,
  content: {
    alignItems: "center",
    paddingHorizontal: 28,
  },
  orbSpacer: {
    height: 110,
  },
});

export default InviteGlassCard;
